import { Box, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { CreditPackageSelectedState } from "@/global/store/data/purchaseCredit";
import { GenericPaymentForm } from "../GenericPaymentForm/GenericPaymentForm";
import { CreditPackageItem } from "./CreditPackageItem";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { CreditPackage } from "@/global/store/data/types";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { createPortal } from "react-dom";
import {
  ModalBackIcon,
  ModalCloseIcon,
  ModalTitleText,
} from "@/shared/base/components/ModalReusables/ModalReusables";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { TmAccountDetails } from "../TmAccountDetails/TmAccountDetails";
import { UrlPayload } from "./PurchaseCredit";
import { Scheduler } from "@/global/utils/scheduler";
import { globalConfig } from "@/global/config/global_config";
import { CountDownClock, CountDownClockObserver } from "./CountDownClock";
import React from "react";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";

interface Props {
  isLoading?: boolean;
  selectedCreditPackage: CreditPackageSelectedState;
  creditPackages: CreditPackage[];
  tempUserSession?: UrlPayload["tempUser"];
  onSubmitCheckout: (data: any) => void;
  onCloseCheckoutModal?: () => void;
  setIsCollapseOpen?: (val: boolean) => void;
}

// checkout timer
const { checkoutSchedulerInterval } = globalConfig.purchaseCredit;
const checkoutTimer = new Scheduler({ delay: checkoutSchedulerInterval });

export function PurchaseCreditCheckout(props: Props) {
  const teamConfig = useTeamConfig();
  const { checkoutTimerEnabled } = teamConfig.purchaseCreditConfigs;
  const { router, theme } = useAppSystem();
  const { md: mdDown } = useCssMediaQuery("down");
  const serviceFee = props?.selectedCreditPackage?.serviceFee || 0;

  const renderCreditPackageItem = () => {
    if (props.selectedCreditPackage) {
      const { isCustom } = props.selectedCreditPackage;
      const selectedPackageData = props.creditPackages.find((itm) => {
        return itm.id == props.selectedCreditPackage?.id;
      });

      return (
        <CreditPackageItem
          hideButton
          amount={props?.selectedCreditPackage?.amount}
          title={isCustom ? "Custom Amount" : selectedPackageData?.title}
          onSelect={() => {}}
          isSelected={true}
          isDisabled
          backgroundColor={selectedPackageData?.backgroundColor}
          description={selectedPackageData?.description}
        />
      );
    }
    return null;
  };

  React.useEffect(() => {
    if (checkoutTimerEnabled) {
      // subscribing to timer to cancel cart automatically
      checkoutTimer.subscribe(() => props?.onCloseCheckoutModal?.());
      checkoutTimer.start();
      return () => checkoutTimer.reset();
    }
  }, []);

  return (
    <Stack direction={mdDown ? "column" : "row"} height={"100%"}>
      <Box width={"100%"}>
        {props.isLoading ? (
          <Stack p={mdDown ? 2 : 4} spacing={mdDown ? 2 : 4}>
            {Array.from({ length: 2 })
              .fill(1)
              .map((itm, idx) => {
                return (
                  <DynamicSkeleton
                    key={idx}
                    rowCount={4}
                    spacing={2}
                    widths={["100%", "100%", "80%", "60%"]}
                    heights={["110px", "20px", "20px", "20px"]}
                  />
                );
              })}
          </Stack>
        ) : null}

        {!props.isLoading ? (
          <Stack spacing={1} height={"100%"}>
            <Stack p={mdDown ? 2 : 4} spacing={3}>
              {props.tempUserSession ? (
                <Box>
                  <TmAccountDetails
                    email={props?.tempUserSession?.email}
                    accountId={props?.tempUserSession?.accountId}
                  />
                </Box>
              ) : null}

              <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={600} lineHeight={1}>
                  Your Items
                </Typography>
                {renderCreditPackageItem()}
              </Stack>
            </Stack>
            {!mdDown ? <Box flexGrow={1} /> : null}
            <Divider />

            {serviceFee > 0 ? (
              <Stack
                px={mdDown ? 2 : 4}
                pt={2}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={"text.secondary"}
                >
                  Service Fee
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {getUSCurrency(serviceFee)}
                </Typography>
              </Stack>
            ) : null}

            {props?.selectedCreditPackage?.amount ? (
              <Stack
                px={mdDown ? 2 : 4}
                pt={1}
                pb={2}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  color={"text.secondary"}
                >
                  Total Due
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {getUSCurrency(
                    props?.selectedCreditPackage?.amount + serviceFee
                  )}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        ) : null}
      </Box>

      <Box>
        <Divider orientation="vertical" />
      </Box>

      <Box width={"100%"} p={mdDown ? 2 : 4}>
        {checkoutTimerEnabled ? (
          <Stack alignItems={"flex-end"} mb={1}>
            <CountDownClock />
          </Stack>
        ) : null}

        <GenericPaymentForm
          formId="purchase-credit-checkout-form"
          submitButtonId="purchase-credit-submit"
          isPaymentDue={true}
          isZipVerificationEnabled={true}
          tempUser={props?.tempUserSession}
          setIsCollapseOpen={props?.setIsCollapseOpen}
          onSubmit={async (ev) => {
            if (checkoutTimerEnabled) {
              checkoutTimer.stop();
              await props.onSubmitCheckout?.(ev);
              checkoutTimer.resume();
              CountDownClockObserver.shouldReset = true;
            } else {
              await props.onSubmitCheckout?.(ev);
            }
          }}
        />
      </Box>
    </Stack>
  );
}

interface PurchaseCreditCheckoutModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  renderBody: () => React.ReactNode;
  buttonText: string;
  title: string;
}

export const PurchaseCreditCheckoutModal = (
  props: PurchaseCreditCheckoutModalProps
) => {
  const { theme } = useAppSystem();
  const { md: mdDown } = useCssMediaQuery("down");

  if (!props.isOpen) return null;
  return createPortal(
    <Box
      width={"100%"}
      height={"100vh"}
      top="0"
      left="0"
      position={"fixed"}
      zIndex={"999"}
      bgcolor={"rgba(0,0,0,0.5)"}
      sx={{ backdropFilter: "blur(5px)" }}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      <Stack
        maxWidth={"1100px"}
        maxHeight={mdDown ? "100%" : "790px"}
        width={"100%"}
        height="100%"
        borderRadius={mdDown ? "0px" : "6px"}
        bgcolor={theme.palette.background.paper}
        sx={{
          transition: "all 0.3s ease-in-out",
          transform: props.isOpen ? "translateY(0)" : "translateY(100%)",
        }}
      >
        <Stack
          py={mdDown ? 1 : 2}
          px={mdDown ? 1 : 3}
          spacing={1}
          direction={"row"}
          alignItems={"center"}
        >
          {mdDown ? (
            <ModalBackIcon
              disabled={props?.isSubmitting}
              onClick={props.onClose}
            />
          ) : null}
          <ModalTitleText>{props.title}</ModalTitleText>
          <Box flexGrow={1} />
          {!mdDown ? (
            <ModalCloseIcon
              disabled={props?.isSubmitting}
              onClick={props.onClose}
            />
          ) : null}
        </Stack>
        <Divider />
        <Box flexGrow={1} overflow={"auto"}>
          {props.renderBody()}
        </Box>
        <Divider />
        <Stack
          py={2}
          px={2}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
        >
          <Box minWidth={"180px"}>
            <ModalActionButton
              isLoading={props.isSubmitting}
              disabled={props.isSubmitting}
              variant="primary"
              onClick={() => {
                const submitButton = document.getElementById(
                  "purchase-credit-submit"
                );
                submitButton?.click();
              }}
            >
              {props?.buttonText}
            </ModalActionButton>
          </Box>
        </Stack>
      </Stack>
    </Box>,
    document.body
  );
};
